import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Typography,
    Button,
    Paper,
    Chip,
    IconButton,
    Grid,
    Divider,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    TextField,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
} from '@mui/material';
import {
    RiArrowLeftLine,
    RiInformationLine,
    RiPlayLine,
    RiMoreFill,
    RiComputerLine,
    RiHardDriveLine,
    RiCpuLine,
    RiFileCopyLine,
    RiMonitorLine,
    RiHardDrive3Line,
    RiRam2Line,
    RiMore2Fill,
    RiDeleteBinLine,
    RiRefreshLine,
    RiVideoLine,
} from '@remixicon/react';
import { borderRadius, colors, naturalScale, typography } from '../theme/tokens';
import { isOlderThan24Hours } from '../utils/helpers';
import { updateTVName, reloadTV, clearCacheTV, deleteTVBySerial, updateStatus, updateOrientation } from '../services/firebaseService';
import { readSlideConfig, updateSlideConfig, updateSlideConfig2 } from '../services/s3Service';
import LandscapeSVG from '../assets/images/Landscape.svg';
import PortraitSVG from '../assets/images/Portrait.svg';
import ClearCache from '../assets/images/clear_cache.svg';

const TVDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { tvs: rawTVs } = useSelector((state) => state.tv);
    const { takeawayList } = useSelector((state) => state.auth);
    const [tv, setTV] = useState(null);
    const [orientation, setOrientation] = useState('landscape');
    const [reverseOrientation, setReverseOrientation] = useState(false);
    const [deviceName, setDeviceName] = useState('');
    const [isUpdatingName, setIsUpdatingName] = useState(false);
    const [activeTab, setActiveTab] = useState('device'); // 'device' | 'playing'
    const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
    const [selectedTV, setSelectedTV] = useState(null);
    const [slideMenuAnchor, setSlideMenuAnchor] = useState(null);
    const [selectedSlide, setSelectedSlide] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    // Slides state for "Playing now" tab
    const [slides, setSlides] = useState([]);
    const [slidesLoading, setSlidesLoading] = useState(false);
    const [slidesError, setSlidesError] = useState(null);

    const saveSlides = async (newSlides) => {
        if (!tv) return;
        const storeId = tv.storeId || tv.store_id || tv.activeStoreLicenseKey;
        if (!storeId) return;

        try {
            // Update slideconfig.json
            await updateSlideConfig2(storeId, tv.id, newSlides);

            // Update TV status (slides_updated_at)
            await updateStatus(tv.serialNumber);
        } catch (error) {
            console.error('Failed to save slides:', error);
            throw error;
        }
    };

    const mapOrientationFromValue = (value) => {
        const v = (value || '').toLowerCase();
        switch (v) {
            case 'landscape left':
                return { base: 'landscape', reverse: true };
            case 'portrait up':
                return { base: 'portrait', reverse: false };
            case 'portrait down':
                return { base: 'portrait', reverse: true };
            case 'landscape right':
            default:
                return { base: 'landscape', reverse: false };
        }
    };

    const getOrientationValue = (base, reverse) => {
        if (base === 'landscape') {
            return reverse ? 'landscape left' : 'landscape right';
        }
        return reverse ? 'portrait down' : 'portrait up';
    };

    // Process TVs to determine online/offline status
    useEffect(() => {
        if (rawTVs && rawTVs.length > 0) {
            const foundTV = rawTVs.find((t) => t.id === id);
            if (foundTV) {
                const isActive = !isOlderThan24Hours(foundTV?.lastActive);
                const processedTV = {
                    ...foundTV,
                    isActive,
                    status: isActive ? 'online' : 'offline',
                };
                setTV(processedTV);
                setDeviceName(processedTV.name || '');
                const { base, reverse } = mapOrientationFromValue(processedTV.orientation);
                setOrientation(base);
                setReverseOrientation(reverse);
            }
        }
    }, [rawTVs, id]);

    // Load slides when switching to "playing" tab
    useEffect(() => {
        if (activeTab === 'playing') {
            loadSlides();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, tv?.id]);

    if (!tv) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <Typography>TV not found</Typography>
            </Box>
        );
    }

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        // You could add a toast notification here
    };

    const handleOrientationChange = async (newOrientation) => {
        if (!tv || !newOrientation) return;

        const serialNumber = tv.serialNumber || tv.id;
        if (!serialNumber) return;

        setOrientation(newOrientation);
        const value = getOrientationValue(newOrientation, reverseOrientation);

        try {
            await updateOrientation(serialNumber, value);
            setTV((prev) => (prev ? { ...prev, orientation: value } : prev));
        } catch (error) {
            console.error('Failed to update TV orientation:', error);
        }
    };

    const handleNameUpdate = async () => {
        if (!tv || !deviceName.trim() || deviceName === tv.name || isUpdatingName) {
            return;
        }

        const serialNumber = tv.serialNumber || tv.id;
        if (!serialNumber) {
            console.error('No serial number found for TV');
            return;
        }

        setIsUpdatingName(true);
        try {
            await updateTVName(serialNumber, deviceName.trim());
            // Update local state
            setTV((prev) => ({
                ...prev,
                name: deviceName.trim(),
            }));
        } catch (error) {
            console.error('Failed to update TV name:', error);
            // Revert to original name on error
            setDeviceName(tv.name || '');
            alert(`Failed to update TV name: ${error.message || 'Unknown error'}`);
        } finally {
            setIsUpdatingName(false);
        }
    };

    const handleNameKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.target.blur(); // This will trigger onBlur
        }
    };

    const handleActionClick = (event) => {
        setActionMenuAnchor(event.currentTarget);
        setSelectedTV(tv);
    };

    const handleActionClose = () => {
        setActionMenuAnchor(null);
        setSelectedTV(null);
    };

    const handleRefresh = async (targetTV) => {
        if (!targetTV) return;
        if (window.confirm('Do you want to restart this TV ?!')) {
            try {
                const serialNumber = targetTV.serialNumber || targetTV.id;
                await reloadTV(serialNumber);
                alert('Restart command sent successfully!');
            } catch (error) {
                console.error('Failed to restart TV:', error);
                alert(`Failed to restart TV: ${error.message || 'Unknown error'}`);
            }
        }
    };

    const handleClearCache = async (targetTV) => {
        if (!targetTV) return;
        if (window.confirm('Do you want to clear cache for this TV ?!')) {
            try {
                const serialNumber = targetTV.serialNumber || targetTV.id;
                await clearCacheTV(serialNumber);
                alert('Clear cache command sent successfully!');
            } catch (error) {
                console.error('Failed to clear cache:', error);
                alert(`Failed to clear cache: ${error.message || 'Unknown error'}`);
            }
        }
    };

    const handleDeleteTV = async (targetTV) => {
        if (!targetTV) return;
        if (window.confirm('Do you want to delete this TV ?!')) {
            try {
                const serialNumber = targetTV.serialNumber || targetTV.id;
                await deleteTVBySerial('demo-user', serialNumber);
                alert('TV deleted successfully');
                navigate('/tvs');
            } catch (error) {
                console.error('Failed to delete TV:', error);
                alert(`Failed to delete TV: ${error.message || 'Unknown error'}`);
            }
        }
    };

    const loadSlides = async () => {
        if (!tv) return;
        const storeId = tv.storeId || tv.store_id || tv.activeStoreLicenseKey;
        if (!storeId) {
            setSlides([]);
            setSlidesError('Store ID not found for this TV.');
            return;
        }

        setSlidesLoading(true);
        setSlidesError(null);
        try {
            const config = await readSlideConfig(storeId, tv.id);
            setSlides(Array.isArray(config) ? config : []);
        } catch (error) {
            console.error('Failed to load slides:', error);
            setSlides([]);
            setSlidesError(error.message || 'Failed to load slides.');
        } finally {
            setSlidesLoading(false);
        }
    };

    return (
        <Paper
            sx={{
                p: 2.5,
                mb: 3,
                borderRadius: borderRadius.xxxs,
                border: `1px solid ${colors.neutral.gray200}`,
                // display: 'flex',
                alignItems: 'center',
                gap: 2,
            }}
        >
            {/* <Box> */}
            {/* Header with title, status, and actions */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 4,
                }}
            >
                <Box>
                    <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ mb: 1, color: naturalScale[5] }}
                    >
                        {tv.name}
                    </Typography>
                    <Chip
                        label={tv.status === 'online' ? 'Online' : 'Offline'}
                        size="small"
                        icon={
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: tv.status === 'online' ? colors.neutral.white : colors.neutral.black,
                                }}
                            />
                        }
                        sx={{
                            bgcolor: tv.status === 'online' ? colors.success.dark : '#EBEBED',
                            color: tv.status === 'online' ? colors.neutral.white : colors.neutral.black,
                            fontWeight: 600,
                            borderRadius: '5px',
                            '& .MuiChip-icon': {
                                ml: 1,
                            },
                        }}
                    />
                </Box>
                {/* Tabs: Device information / Playing now */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            bgcolor: '#F5F5F7',
                            borderRadius: borderRadius.xxxs,
                            p: 0.5,
                            gap: 1,
                        }}
                    >
                        <Button
                            startIcon={<RiInformationLine size={18} />}
                            onClick={() => setActiveTab('device')}
                            sx={{
                                textTransform: 'none',
                                borderRadius: borderRadius.xxxs,
                                px: 3,
                                py: 1,
                                fontWeight: activeTab === 'device' ? 600 : 500,
                                fontSize: 14,
                                bgcolor: activeTab === 'device' ? colors.neutral.white : 'transparent',
                                color: activeTab === 'device' ? colors.neutral.black : naturalScale[65],
                                boxShadow: activeTab === 'device' ? '0 0 0 1px rgba(0,0,0,0.02)' : 'none',
                                '&:hover': {
                                    bgcolor: activeTab === 'device' ? colors.neutral.white : 'transparent',
                                },
                            }}
                        >
                            Device information
                        </Button>
                        <Button
                            startIcon={<RiVideoLine size={18} />}
                            onClick={() => setActiveTab('playing')}
                            sx={{
                                textTransform: 'none',
                                borderRadius: borderRadius.xxxs,
                                px: 3,
                                py: 1,
                                fontWeight: activeTab === 'playing' ? 600 : 500,
                                fontSize: 14,
                                bgcolor: activeTab === 'playing' ? colors.neutral.white : 'transparent',
                                color: activeTab === 'playing' ? colors.neutral.black : naturalScale[65],
                                boxShadow: activeTab === 'playing' ? '0 0 0 1px rgba(0,0,0,0.02)' : 'none',
                                '&:hover': {
                                    bgcolor: activeTab === 'playing' ? colors.neutral.white : 'transparent',
                                },
                            }}
                        >
                            Playing now
                        </Button>
                    </Box>
                    <IconButton
                        sx={{ width: 48, height: 48, border: `1px solid ${colors.neutral.gray200}`, borderRadius: borderRadius.xxxs }}
                        onClick={handleActionClick}
                    >
                        <RiMore2Fill size={24} color={'#1D2632'} />
                    </IconButton>
                </Box>

            </Box>

            {/* Main Content */}
            {activeTab === 'device' && (
                <Grid container spacing={3}>
                    {/* Left Column - Device Information */}
                    <Grid item xs={12} md={6}>
                        {/* Device Name Card */}
                        <Paper
                            sx={{
                                p: 2.5,
                                mb: 3,
                                borderRadius: borderRadius.xxxs,
                                border: `1px solid ${colors.neutral.gray200}`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                height: 60,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: borderRadius.xxxs,
                                    bgcolor: naturalScale[150],
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <RiComputerLine size={24} color={naturalScale[50]} />
                            </Box>
                            <TextField
                                value={deviceName}
                                onChange={(e) => setDeviceName(e.target.value)}
                                onBlur={handleNameUpdate}
                                onKeyDown={handleNameKeyDown}
                                disabled={isUpdatingName}
                                variant="standard"
                                InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                        fontSize: '1.25rem',
                                        fontWeight: 600,
                                        '& input': {
                                            p: 0,
                                            textAlign: 'left',
                                        },
                                    },
                                }}
                                sx={{
                                    flex: 1,
                                    '& .MuiInputBase-root': {
                                        '&:hover': {
                                            bgcolor: naturalScale[150],
                                            borderRadius: borderRadius.xxxs,
                                            px: 1,
                                            py: 0.5,
                                        },
                                        '&.Mui-focused': {
                                            bgcolor: naturalScale[150],
                                            borderRadius: borderRadius.xxxs,
                                            px: 1,
                                            py: 0.5,
                                        },
                                    },
                                }}
                            />
                        </Paper>

                        {/* Device Information Section */}
                        <Paper
                            sx={{
                                p: 2,
                                mb: 3,
                                borderRadius: borderRadius.xxxs,
                                border: `1px solid ${colors.neutral.gray200}`,
                                backgroundColor: naturalScale[148]
                            }}
                        >
                            <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{ mb: 2, color: naturalScale[5] }}
                            >
                                Device information
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 1.5,
                                    borderBottom: `1px solid ${colors.neutral.gray200}`,
                                }}
                            >
                                <Typography variant="subtitle2" color="text.secondary">
                                    Device ID
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                    <Typography variant="body1" fontWeight={600}>
                                        {tv.id || 'N/A'}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleCopy(tv.id || 'N/A')}
                                        sx={{ color: naturalScale[50] }}
                                    >
                                        <RiFileCopyLine size={24} />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 1.5,
                                    borderBottom: `1px solid ${colors.neutral.gray200}`,
                                }}
                            >
                                <Typography variant="subtitle2" color="text.secondary">
                                    APK Version
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                    <Typography variant="body1" fontWeight={600}>
                                        {tv.appVersion || 'N/A'}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleCopy(tv.appVersion || 'N/A')}
                                        sx={{ color: naturalScale[50] }}
                                    >
                                        <RiFileCopyLine size={24} />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 1.5,
                                    borderBottom: `1px solid ${colors.neutral.gray200}`,
                                }}
                            >
                                <Typography variant="subtitle2" color="text.secondary">
                                    Manufacturer
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                    <Typography variant="body1" fontWeight={600}>
                                        {tv.manufacturer || 'N/A'}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleCopy(tv.manufacturer || 'N/A')}
                                        sx={{ color: naturalScale[50] }}
                                    >
                                        <RiFileCopyLine size={24} />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 1.5,
                                    borderBottom: `1px solid ${colors.neutral.gray200}`,
                                }}
                            >
                                <Typography variant="subtitle2" color="text.secondary">
                                    System Name
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                    <Typography variant="body1" fontWeight={600}>
                                        {tv.systemName || 'N/A'}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleCopy(tv.systemName || 'N/A')}
                                        sx={{ color: naturalScale[50] }}
                                    >
                                        <RiFileCopyLine size={24} />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 1.5,
                                }}
                            >
                                <Typography variant="subtitle2" color="text.secondary">
                                    Private IP
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>

                                    <Typography variant="body1" fontWeight={600}>
                                        {tv.ip || 'N/A'}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleCopy(tv.ip || 'N/A')}
                                        sx={{ color: naturalScale[50] }}
                                    >
                                        <RiFileCopyLine size={24} />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Paper>

                        {/* Storage and Memory Cards */}
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Paper
                                    sx={{
                                        p: 2.5,
                                        borderRadius: borderRadius.xxxs,
                                        border: `1px solid ${colors.neutral.gray200}`,
                                        backgroundColor: naturalScale[148]
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                        <RiHardDrive3Line size={24} color={naturalScale[50]} />
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            Storage
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                                        {(tv?.totalDisk - tv?.freeDisk).toFixed(2)} GB Used
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Of {tv?.totalDisk} GB
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6}>
                                <Paper
                                    sx={{
                                        p: 2.5,
                                        borderRadius: borderRadius.xxxs,
                                        border: `1px solid ${colors.neutral.gray200}`,
                                        backgroundColor: naturalScale[148]
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                        <RiRam2Line size={24} color={naturalScale[50]} />
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            Memory
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                                        {tv?.usedMemory} GB Used
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Of {tv?.totalMemory} GB
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Right Column - Display Preview and Orientation */}
                    <Grid item xs={12} md={6}>
                        {/* Display Preview */}
                        <Paper
                            sx={{
                                p: 3,
                                mb: 3,
                                borderRadius: borderRadius.xxxs,
                                // border: `1px solid ${colors.neutral.gray200}`,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <Box
                                component="img"
                                src={orientation === 'landscape' ? LandscapeSVG : PortraitSVG}
                                alt="No media"
                                sx={{
                                    height: 'auto',
                                    objectFit: 'contain',
                                    mb: 3,
                                    // transform:
                                    //     orientation === 'landscape'
                                    //         ? reverseOrientation
                                    //             ? 'scaleX(-1)'
                                    //             : 'none'
                                    //         : reverseOrientation
                                    //         ? 'rotate(180deg)'
                                    //         : 'none',
                                }}
                            />

                            {/* Orientation Selection */}
                            <Box sx={{ width: '100%', mb: 3 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        bgcolor: '#F5F5F7',
                                        borderRadius: 999,
                                        p: 0.75,
                                        gap: 0,
                                        // minWidth: 486,
                                        borderRadius: borderRadius.xxxs,
                                        alignSelf: 'center',
                                    }}
                                >
                                    <Box
                                        onClick={() => handleOrientationChange('landscape')}
                                        sx={{
                                            flex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: borderRadius.xxxs,
                                            bgcolor: orientation === 'landscape' ? colors.neutral.white : 'transparent',
                                            color: orientation === 'landscape' ? colors.neutral.black : naturalScale[65],
                                            fontWeight: 600,
                                            fontSize: 16,
                                            py: 1.5,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        Landscape
                                    </Box>
                                    <Box
                                        onClick={() => handleOrientationChange('portrait')}
                                        sx={{
                                            flex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: borderRadius.xxxs,
                                            bgcolor: orientation === 'portrait' ? colors.neutral.white : 'transparent',
                                            color: orientation === 'portrait' ? colors.neutral.black : naturalScale[65],
                                            fontWeight: orientation === 'portrait' ? 700 : 500,
                                            fontSize: 16,
                                            py: 1.5,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        Portrait
                                    </Box>
                                </Box>
                            </Box>

                            {/* Reverse Orientation Toggle */}
                            <Box
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    p: 2,
                                    borderRadius: borderRadius.xxxs,
                                    bgcolor: naturalScale[150],
                                }}
                            >
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                                        Reverse Orientation
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        This Option Will Flip Orientation To The Opposite Direction
                                    </Typography>
                                </Box>
                                <Switch
                                    checked={reverseOrientation}
                                    onChange={async (e) => {
                                        const checked = e.target.checked;
                                        setReverseOrientation(checked);
                                        if (!tv) return;
                                        const serialNumber = tv.serialNumber || tv.id;
                                        if (!serialNumber) return;
                                        const value = getOrientationValue(orientation, checked);
                                        try {
                                            await updateOrientation(serialNumber, value);
                                            setTV((prev) => (prev ? { ...prev, orientation: value } : prev));
                                        } catch (error) {
                                            console.error('Failed to update TV orientation:', error);
                                        }
                                    }}
                                    sx={{
                                        width: 52,
                                        height: 32,
                                        padding: 0,
                                        '& .MuiSwitch-switchBase': {
                                            padding: 1,
                                            '&.Mui-checked': {
                                                transform: 'translateX(20px)',
                                                color: '#fff',
                                            },
                                        },
                                        '& .MuiSwitch-thumb': {
                                            width: 18,
                                            height: 18,
                                            boxShadow: 'none',
                                            bgcolor: '#6B7178', // gray knob
                                        },
                                        '& .MuiSwitch-track': {
                                            borderRadius: 26,
                                            border: '2px solid #6B7178',
                                            bgcolor: 'transparent',
                                            opacity: 1,
                                        },
                                    }}
                                />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {activeTab === 'playing' && (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Paper>
                            {slidesLoading && (
                                <Typography variant="body2" color="text.secondary">
                                    Loading slides...
                                </Typography>
                            )}

                            {slidesError && !slidesLoading && (
                                <Typography variant="body2" color="error">
                                    {slidesError}
                                </Typography>
                            )}

                            {!slidesLoading && !slidesError && slides.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    No slides assigned to this TV yet.
                                </Typography>
                            )}

                            {!slidesLoading && slides.length > 0 && (
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    {slides.map((slide) => {
                                        const key = slide.id || slide.uri || slide.videoUrl;
                                        const isImage = slide.type === 'Image' && slide.uri;
                                        const isVideo = slide.type === 'Video' && (slide.previewUrl || slide.videoUrl);
                                        const src = isImage ? slide.uri : slide.previewUrl || slide.videoUrl;

                                        return (
                                            <Grid item xs={12} sm={4} md={2.5} key={key}>
                                                <Paper
                                                    sx={{
                                                        p: 1,
                                                        borderRadius: borderRadius.xxxs,
                                                        border: `1px solid ${colors.neutral.gray200}`,
                                                        cursor: 'pointer',
                                                        position: 'relative',
                                                        '&:hover': {
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                                        },
                                                        width: 260,
                                                        height: 200,
                                                    }}
                                                    onClick={() => {
                                                        setSelectedSlide(slide);
                                                        setPreviewOpen(true);
                                                    }}
                                                >
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}>
                                                        <Typography variant="subtitle1" fontWeight={600}>{slide.name || slide.title || slide.type || 'Slide'}</Typography>
                                                        <IconButton
                                                            size="small"
                                                            sx={{

                                                            }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSlideMenuAnchor(e.currentTarget);
                                                                setSelectedSlide(slide);
                                                            }}
                                                        >
                                                            <RiMore2Fill size={24} color={'#1D2632'} />
                                                        </IconButton>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            aspectRatio: '16/9',
                                                            borderRadius: borderRadius.xxxs,
                                                            overflow: 'hidden',
                                                            bgcolor: colors.neutral.gray50,
                                                            mb: 1,
                                                        }}
                                                    >
                                                        {isImage ? (
                                                            <Box
                                                                component="img"
                                                                src={src}
                                                                alt={slide.name || 'Slide'}
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                }}
                                                            />
                                                        ) : (
                                                            <Box
                                                                component="video"
                                                                src={src}
                                                                muted
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            )}
            {/* TV Actions Menu */}
            <Menu
                anchorEl={actionMenuAnchor}
                open={Boolean(actionMenuAnchor)}
                onClose={handleActionClose}
            >
                <MenuItem
                    onClick={() => {
                        handleRefresh(selectedTV);
                        handleActionClose();
                    }}
                >
                    <RiRefreshLine size={18} style={{ marginRight: 8 }} />
                    Reload Screen
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClearCache(selectedTV);
                        handleActionClose();
                    }}
                >
                    <Box
                        component="img"
                        src={ClearCache}
                        alt="Clear Cache"
                        sx={{
                            width: 18,
                            maxWidth: 18,
                            height: 'auto',
                            objectFit: 'contain',
                            marginRight: 1
                        }}
                    />
                    Clear Cache
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleDeleteTV(selectedTV);
                        handleActionClose();
                    }}
                    sx={{ color: colors.error.main }}
                >
                    <RiDeleteBinLine size={18} style={{ marginRight: 8 }} />
                    Delete
                </MenuItem>
            </Menu>

            {/* Slide Actions Menu */}
            <Menu
                anchorEl={slideMenuAnchor}
                open={Boolean(slideMenuAnchor)}
                onClose={() => {
                    setSlideMenuAnchor(null);
                    setSelectedSlide(null);
                }}
            >
                <MenuItem
                    onClick={() => {
                        setSlideMenuAnchor(null);
                        if (selectedSlide) {
                            setPreviewOpen(true);
                        }
                    }}
                >
                    Preview
                </MenuItem>
                <MenuItem
                    onClick={async () => {
                        if (!selectedSlide) return;
                        const newSlides = slides.filter((s) => s !== selectedSlide);
                        setSlideMenuAnchor(null);
                        setSelectedSlide(null);
                        setSlides(newSlides);
                        try {
                            await saveSlides(newSlides);
                        } catch (e) {
                            alert('Failed to update slides. Please try again.');
                        }
                    }}
                    sx={{ color: colors.error.main }}
                >
                    Delete
                </MenuItem>
            </Menu>

            {/* Slide Preview Modal */}
            <Dialog
                open={previewOpen && !!selectedSlide}
                onClose={() => setPreviewOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogContent
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: colors.neutral.gray900,
                    }}
                >
                    {selectedSlide && selectedSlide.type === 'Image' && selectedSlide.uri && (
                        <Box
                            component="img"
                            src={selectedSlide.uri}
                            alt={selectedSlide.name || 'Slide'}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                objectFit: 'contain',
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                MozUserSelect: 'none',
                                msUserSelect: 'none',
                                WebkitUserDrag: 'none',
                                pointerEvents: 'auto',
                            }}
                            draggable={false}
                            onContextMenu={(e) => e.preventDefault()}
                            onDragStart={(e) => e.preventDefault()}
                            onSelectStart={(e) => e.preventDefault()}
                        />
                    )}
                    {selectedSlide && selectedSlide.type === 'Video' && (selectedSlide.previewUrl || selectedSlide.videoUrl) && (
                        <Box
                            component="video"
                            src={selectedSlide.previewUrl || selectedSlide.videoUrl}
                            controls
                            autoPlay
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                borderRadius: borderRadius.xxxs,
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                MozUserSelect: 'none',
                                msUserSelect: 'none',
                            }}
                            controlsList="nodownload"
                            disablePictureInPicture
                            onContextMenu={(e) => e.preventDefault()}
                            onDragStart={(e) => e.preventDefault()}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Paper>
    );
};

export default TVDetails;

